import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Phone, Mail, Pencil, Trash2, ChevronDown, ChevronRight,
  Save, X, MapPin, FileText, Users as UsersIcon, Calendar,
} from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { formatDateBE, parseDate } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";

/**
 * Follow-through — handmatige watchlist van eigenaars die op termijn willen
 * verkopen. Gegroepeerd in collapsible buckets per tijdschatting (kortste
 * termijn bovenaan).
 */
const TIMEFRAMES = [
  { key: "3m",      label: "3 maanden",   sub: "Kort op de bal", accent: "ready" },
  { key: "6m",      label: "6 maanden",   sub: "Binnen halfjaar", accent: "ready" },
  { key: "1y",      label: "1 jaar",      sub: "Op koers voor volgend jaar", accent: "ripening" },
  { key: "2y",      label: "2 jaar",      sub: "Lange termijn", accent: "ripening" },
  { key: "3y_plus", label: "3+ jaar",     sub: "Verre toekomst", accent: "fresh" },
  { key: "unknown", label: "Onbepaald",   sub: "Geen tijdschatting", accent: "fresh" },
];
const TIMEFRAME_KEYS = TIMEFRAMES.map(x => x.key);

/** DD/MM/YYYY → YYYY-MM-DD voor HTML5 <input type="date">. */
function toIsoInput(s) {
  const d = parseDate(s);
  if (!d || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
/** YYYY-MM-DD → DD/MM/YYYY voor backend opslag. */
function fromIsoInput(s) {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}
function todayBE() {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${m}/${d.getFullYear()}`;
}

const buildEmptyForm = () => ({
  address: "", postal_code: "", city: "",
  owner_name: "", owner_phone: "", owner_email: "",
  timeframe: "6m", reference_date: todayBE(), notes: "",
});

export default function FollowThrough() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | entry obj
  const [form, setForm] = useState(buildEmptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("followThrough.collapsed") || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("followThrough.collapsed", JSON.stringify(collapsed)); }
    catch (e) { console.debug("localStorage write failed (followThrough.collapsed)", e); }
  }, [collapsed]);

  const refresh = async () => {
    setLoading(true);
    const r = await http.get("/api/follow-through");
    setEntries(r.data);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return entries;
    const q = query.toLowerCase();
    return entries.filter(e =>
      e.address?.toLowerCase().includes(q) ||
      e.postal_code?.includes(q) ||
      e.city?.toLowerCase().includes(q) ||
      e.owner_name?.toLowerCase().includes(q) ||
      e.owner_phone?.toLowerCase().includes(q) ||
      e.owner_email?.toLowerCase().includes(q) ||
      e.notes?.toLowerCase().includes(q)
    );
  }, [entries, query]);

  const buckets = useMemo(() => {
    const map = Object.fromEntries(TIMEFRAME_KEYS.map(k => [k, []]));
    for (const e of filtered) {
      // current_timeframe wordt door de backend berekend o.b.v. reference_date + timeframe
      const k = TIMEFRAME_KEYS.includes(e.current_timeframe) ? e.current_timeframe : "unknown";
      map[k].push(e);
    }
    return map;
  }, [filtered]);

  const startNew = () => { setEditing("new"); setForm(buildEmptyForm()); };
  const startEdit = (e) => {
    setEditing(e);
    setForm({
      address: e.address || "",
      postal_code: e.postal_code || "",
      city: e.city || "",
      owner_name: e.owner_name || "",
      owner_phone: e.owner_phone || "",
      owner_email: e.owner_email || "",
      timeframe: e.timeframe || "unknown",
      reference_date: e.reference_date || todayBE(),
      notes: e.notes || "",
    });
  };
  const cancelEdit = () => { setEditing(null); setForm(buildEmptyForm()); };

  const save = async () => {
    if (!form.address.trim()) return;
    setSaving(true);
    try {
      if (editing === "new") {
        await http.post("/api/follow-through", form);
      } else if (editing) {
        await http.patch(`/api/follow-through/${editing.id}`, form);
      }
      cancelEdit();
      refresh();
    } catch (err) {
      const msg = err.response?.data?.detail || "Opslaan mislukt";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const id = confirmDelete?.id;
    setConfirmDelete(null);
    if (!id) return;
    await http.delete(`/api/follow-through/${id}`);
    refresh();
  };

  return (
    <div className="flex-1 min-w-0 p-8 overflow-auto" data-testid="follow-through-screen">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            Pipeline
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            Follow-through
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1 max-w-2xl">
            Handmatig beheerde watchlist van eigenaars die twijfelen om op termijn te verkopen.
            Gegroepeerd op tijdschatting — kortste termijn bovenaan.
          </p>
        </div>
        <button type="button" onClick={startNew}
                data-testid="btn-follow-new"
                className="btn btn-primary h-10 !px-4">
          <Plus size={15} /> Nieuwe lead
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
          <input
            data-testid="follow-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`${t("common.search")}…`}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm focus:border-[var(--teal-500)] outline-none"
          />
        </div>
        <div className="text-[12px] text-[var(--ink-500)]" data-testid="follow-total-count">
          {entries.length} {entries.length === 1 ? "lead" : "leads"} totaal
        </div>
      </div>

      {loading ? (
        <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16 px-6" data-testid="follow-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[var(--teal-50)] text-[var(--teal-700)] grid place-items-center mb-4">
            <UsersIcon size={22} />
          </div>
          <h3 className="font-display text-[20px]">
            {query ? "Geen resultaten" : "Nog geen follow-through leads"}
          </h3>
          <p className="text-[14px] text-[var(--ink-500)] mt-2 max-w-md mx-auto">
            {query
              ? "Pas de zoekopdracht aan of voeg een nieuwe lead toe."
              : "Voeg eigenaars toe die op termijn willen verkopen, met een tijdschatting (3 maand → 3+ jaar)."}
          </p>
          {!query && (
            <button type="button" onClick={startNew}
                    data-testid="btn-follow-empty-new"
                    className="btn btn-primary mt-5 h-10 !px-4">
              <Plus size={15} /> Eerste lead toevoegen
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6" data-testid="follow-list">
          {TIMEFRAMES.map(tf => {
            const items = buckets[tf.key];
            if (!items.length) return null;
            const open = collapsed[tf.key] === undefined ? true : !collapsed[tf.key];
            return (
              <div key={tf.key} data-testid={`follow-bucket-${tf.key}`}>
                <button type="button"
                        onClick={() => setCollapsed(c => ({ ...c, [tf.key]: open }))}
                        data-testid={`follow-bucket-toggle-${tf.key}`}
                        className="w-full flex items-baseline justify-between mb-3 text-left hover:opacity-80 transition-opacity">
                  <div className="flex items-center gap-2">
                    {open ? <ChevronDown size={14} className="text-[var(--ink-400)]" />
                          : <ChevronRight size={14} className="text-[var(--ink-400)]" />}
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      tf.accent === "ready" ? "bg-[var(--teal-600)]"
                        : tf.accent === "ripening" ? "bg-[#E5C9A0]"
                          : "bg-[var(--ink-300)]"
                    }`} />
                    <h3 className={`font-display text-[18px] ${
                      tf.accent === "ready" ? "text-[var(--teal-800)]" : "text-[var(--ink-900)]"
                    }`}>{tf.label}</h3>
                    <span className="text-[12px] text-[var(--ink-500)]">({items.length})</span>
                  </div>
                  <span className="text-[11px] text-[var(--ink-400)] uppercase tracking-wider">
                    {tf.sub}
                  </span>
                </button>
                {open && (
                  <ul className="space-y-3">
                    {items.map(e => (
                      <FollowRow key={e.id} entry={e}
                                 onOpen={() => navigate(`/follow-through/${e.id}`)}
                                 onEdit={() => startEdit(e)}
                                 onDelete={() => setConfirmDelete(e)} />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <FollowFormDialog
          mode={editing === "new" ? "new" : "edit"}
          form={form}
          setForm={setForm}
          onCancel={cancelEdit}
          onSave={save}
          saving={saving}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        danger
        title="Lead verwijderen"
        message={confirmDelete
          ? `Weet u zeker dat u "${confirmDelete.address}" wilt verwijderen? Dit kan niet ongedaan gemaakt worden.`
          : ""}
        confirmLabel="Verwijderen"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function FollowRow({ entry, onOpen, onEdit, onDelete }) {
  // Bucket-chip toont de HUIDIGE bucket (dynamisch berekend door backend).
  const currentTf = TIMEFRAMES.find(x => x.key === entry.current_timeframe);
  const storedTf = TIMEFRAMES.find(x => x.key === entry.timeframe);
  const moved = entry.current_timeframe && entry.timeframe
    && entry.current_timeframe !== entry.timeframe
    && entry.timeframe !== "unknown";
  const stopProp = (handler) => (e) => { e.stopPropagation(); handler(); };
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="card !p-4 grid grid-cols-12 gap-4 items-start cursor-pointer hover:border-[var(--teal-400)] hover:shadow-[0_8px_24px_-16px_rgba(14,90,94,0.25)] transition-all"
      data-testid={`follow-row-${entry.id}`}>
      <div className="col-span-5 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className={`chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                           ${currentTf?.accent === "ready" ? "chip-teal"
                             : currentTf?.accent === "ripening" ? "bg-[#FCEBC9] text-[#7C5A1E] border-[#E5C9A0]"
                             : "bg-[var(--sand-100)] text-[var(--ink-700)] border-[var(--border-soft)]"}`}
                data-testid={`follow-row-timeframe-${entry.id}`}>
            {currentTf?.label || entry.current_timeframe || "Onbepaald"}
          </span>
          {moved && (
            <span className="text-[10px] text-[var(--ink-400)] uppercase tracking-wider"
                  title={`Plan was: ${storedTf?.label || entry.timeframe}`}>
              ← was {storedTf?.label || entry.timeframe}
            </span>
          )}
        </div>
        <div className="text-[15px] font-semibold text-[var(--ink-900)] truncate flex items-center gap-1.5">
          <MapPin size={13} className="text-[var(--ink-400)] shrink-0" />
          {entry.address}
        </div>
        {(entry.postal_code || entry.city) && (
          <div className="text-[12px] text-[var(--ink-500)] mt-0.5 ml-[18px]">
            {[entry.postal_code, entry.city].filter(Boolean).join(" ")}
          </div>
        )}
        <div className="text-[11px] text-[var(--ink-500)] mt-1.5 ml-[18px] flex items-center gap-1.5"
             data-testid={`follow-row-meta-${entry.id}`}>
          <Calendar size={11} className="text-[var(--ink-400)]" />
          {entry.reference_date ? formatDateBE(entry.reference_date) : "geen datum"}
          {entry.timeframe && entry.timeframe !== "unknown" && (
            <span className="text-[var(--ink-400)]">· plan: {storedTf?.label || entry.timeframe}</span>
          )}
        </div>
        {entry.notes && (
          <div className="text-[12px] text-[var(--ink-700)] mt-2 flex items-start gap-1.5 leading-relaxed">
            <FileText size={12} className="text-[var(--ink-400)] shrink-0 mt-0.5" />
            <span className="line-clamp-2">{entry.notes}</span>
          </div>
        )}
      </div>
      <div className="col-span-5 min-w-0 text-[13px] text-[var(--ink-700)] space-y-1">
        {entry.owner_name && (
          <div className="font-semibold text-[var(--ink-900)]">{entry.owner_name}</div>
        )}
        {entry.owner_phone && (
          <a href={`tel:${entry.owner_phone}`} onClick={(e) => e.stopPropagation()}
             className="flex items-center gap-1.5 hover:text-[var(--teal-600)]">
            <Phone size={12} /> {entry.owner_phone}
          </a>
        )}
        {entry.owner_email && (
          <a href={`mailto:${entry.owner_email}`} onClick={(e) => e.stopPropagation()}
             className="flex items-center gap-1.5 hover:text-[var(--teal-600)] break-all">
            <Mail size={12} /> {entry.owner_email}
          </a>
        )}
        {!entry.owner_name && !entry.owner_phone && !entry.owner_email && (
          <span className="text-[var(--ink-400)] italic">Geen contactgegevens</span>
        )}
      </div>
      <div className="col-span-2 flex justify-end items-start gap-1">
        <button type="button" onClick={stopProp(onEdit)}
                data-testid={`btn-follow-edit-${entry.id}`}
                title="Bewerken"
                className="p-2 rounded-md text-[var(--ink-500)] hover:bg-[var(--sand-100)] hover:text-[var(--teal-700)]">
          <Pencil size={14} />
        </button>
        <button type="button" onClick={stopProp(onDelete)}
                data-testid={`btn-follow-delete-${entry.id}`}
                title="Verwijderen"
                className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]">
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

function FollowFormDialog({ mode, form, setForm, onCancel, onSave, saving }) {
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const canSave = form.address.trim().length > 0 && !saving;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 backdrop-blur-sm p-4"
         data-testid="follow-form-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[560px] max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[var(--border-soft)]">
          <h2 className="font-display text-[20px]">
            {mode === "new" ? "Nieuwe follow-through lead" : "Lead bewerken"}
          </h2>
          <button type="button" onClick={onCancel}
                  data-testid="btn-follow-form-close"
                  className="p-1.5 rounded-md text-[var(--ink-500)] hover:bg-[var(--sand-100)]">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <Field label="Adres *" required>
            <input data-testid="follow-form-address" value={form.address} onChange={set("address")}
                   placeholder="bv. Kerkstraat 12"
                   className={_inputCls} autoFocus />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Postcode">
              <input data-testid="follow-form-postal" value={form.postal_code} onChange={set("postal_code")}
                     placeholder="2830" className={_inputCls} />
            </Field>
            <div className="col-span-2">
              <Field label="Gemeente">
                <input data-testid="follow-form-city" value={form.city} onChange={set("city")}
                       placeholder="Willebroek" className={_inputCls} />
              </Field>
            </div>
          </div>
          <Field label="Naam eigenaar">
            <input data-testid="follow-form-owner-name" value={form.owner_name} onChange={set("owner_name")}
                   placeholder="bv. Jan Janssens" className={_inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefoon">
              <input data-testid="follow-form-owner-phone" value={form.owner_phone} onChange={set("owner_phone")}
                     placeholder="+32 470 ..." className={_inputCls} />
            </Field>
            <Field label="E-mail">
              <input data-testid="follow-form-owner-email" value={form.owner_email} onChange={set("owner_email")}
                     placeholder="naam@voorbeeld.be" type="email" className={_inputCls} />
            </Field>
          </div>
          <Field label="Tijdschatting *">
            <select data-testid="follow-form-timeframe" value={form.timeframe} onChange={set("timeframe")}
                    className={_inputCls}>
              {TIMEFRAMES.map(tf => (
                <option key={tf.key} value={tf.key}>{tf.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Referentiedatum">
            <input data-testid="follow-form-reference-date"
                   type="date"
                   value={toIsoInput(form.reference_date)}
                   onChange={(e) => setForm(f => ({ ...f, reference_date: fromIsoInput(e.target.value) }))}
                   className={_inputCls} />
            <div className="text-[11px] text-[var(--ink-500)] mt-1.5">
              Datum waarop de eigenaar je deze tijdschatting gaf. De huidige bucket
              wordt automatisch berekend uit deze datum + de tijdschatting.
            </div>
          </Field>
          <Field label="Notities">
            <textarea data-testid="follow-form-notes" value={form.notes} onChange={set("notes")}
                      rows={4} placeholder="bv. Wil eerst kinderen laten verhuizen voor de verkoop"
                      className={`${_inputCls} !h-auto !py-2 resize-y`} />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--border-soft)] bg-[var(--sand-50)]">
          <button type="button" onClick={onCancel}
                  data-testid="btn-follow-form-cancel"
                  className="btn btn-ghost h-9 !px-3">
            {t("common.cancel")}
          </button>
          <button type="button" onClick={onSave} disabled={!canSave}
                  data-testid="btn-follow-form-save"
                  className="btn btn-primary h-9 !px-4 disabled:opacity-50 disabled:cursor-not-allowed">
            <Save size={14} /> {saving ? "Opslaan…" : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1.5 uppercase tracking-wider">
        {label}
      </div>
      {children}
    </label>
  );
}

// shared utility — kept local to avoid touching global stylesheet
const _inputCls = "w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)]";
